/**
 * Build plate capacity for the Bambu Lab A1 (256 x 256 x 256 mm).
 *
 * Uses conservative grid packing on the XY footprint:
 *   - Edge margin on all sides (models shouldn't touch the plate edge)
 *   - Gap between adjacent models (prevents intersection)
 *   - Both orientations are tried (0 deg and 90 deg on the XY plane)
 *
 * Bounding box dimensions are in **centimetres** (as stored in the Quote).
 *   width  = 3MF X extent (plate left/right)
 *   height = 3MF Y extent (plate front/back)
 *   depth  = 3MF Z extent (vertical)
 */

const PLATE_MM = 256;
const PLATE_Z_MM = 256;
const GAP_MM = 5;       // spacing between adjacent models
const EDGE_MM = 5;      // margin from plate edge on each side
const USABLE_MM = PLATE_MM - 2 * EDGE_MM; // 246 mm

interface BoundingBox {
  readonly width: number;   // X in cm
  readonly height: number;  // Y in cm
  readonly depth: number;   // Z in cm
}

/**
 * How many items of size `dimMm` fit along a usable strip of `stripMm`
 * with `gapMm` between each pair.
 *
 *   N items use: N * dimMm + (N-1) * gapMm = N*(dimMm+gapMm) - gapMm
 *   Solve:  N <= (stripMm + gapMm) / (dimMm + gapMm)
 */
const gridFit = (stripMm: number, dimMm: number, gapMm: number): number =>
  dimMm <= 0 ? 0 : Math.max(0, Math.floor((stripMm + gapMm) / (dimMm + gapMm)));

export const calculateMaxPerPlate = (bb: BoundingBox): number => {
  const wMm = bb.width * 10;
  const hMm = bb.height * 10;
  const dMm = bb.depth * 10;

  // Model must fit within the plate Z-height
  if (dMm > PLATE_Z_MM) return 0;

  // Each footprint dimension must fit within the usable area
  if (wMm > USABLE_MM && hMm > USABLE_MM) return 0;

  // Try both orientations on the plate (square plate, but matters
  // when the model is asymmetric and grid counts differ per axis).
  const normalFit =
    gridFit(USABLE_MM, wMm, GAP_MM) * gridFit(USABLE_MM, hMm, GAP_MM);
  const rotatedFit =
    gridFit(USABLE_MM, hMm, GAP_MM) * gridFit(USABLE_MM, wMm, GAP_MM);

  return Math.max(normalFit, rotatedFit);
};

/** Whether a single model can physically be printed (fits in the build volume). */
export const fitsOnPlate = (bb: BoundingBox): boolean =>
  calculateMaxPerPlate(bb) >= 1;
